// EducaMaker por planes — P4 (banner de gracia/corte/past_due).
// Plan maestro: docs/architecture/EDUCAMAKER_ACCESO_PLAN_MAESTRO.md 4.2.
//
// Franja bajo el header del portal para owner y profesores de instituciones
// con plan EducaMaker. Estados (los sirve GET /educamaker/status):
//   grace    -> ambar con cuenta atras (3/2/1 dias; ultimo dia texto propio)
//   cut      -> rojo suave (acceso en pausa; contenido a salvo)
//   past_due -> ambar informativo (impago Stripe en reintentos)
// Descartable por sesion de pagina (reaparece al recargar). CTA por canal:
// comprador Stripe -> renovar en la web; venta directa -> contactar.

import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../hooks/useAuth";
import { ENV } from "../../../Utils/constants";
import { EDUCAMAKER_STATUS_BANNER } from "../../../ui/featureFlags";
import styles from "./EducamakerStatusBanner.module.scss";

// Comprador Stripe -> renovacion self-service en la web de planes.
// Venta directa -> FORMULARIO de contacto (decision Antonio 2026-07-26):
// deja Lead en BD y avisa al equipo comercial, cosa que el mailto anterior
// no hacia. El estado (gracia/corte) viaja como utm_content.
const RENEW_URL =
  "https://planes.educa360.net/planes?utm_source=portal&utm_medium=banner&utm_campaign=educamaker_renew";
const CONTACT_URL =
  "https://planes.educa360.net/enterprise?utm_source=portal&utm_medium=banner&utm_campaign=educamaker_renew";

export function EducamakerStatusBanner() {
  const { t } = useTranslation();
  const { accessToken, user } = useAuth();
  const [status, setStatus] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  const userDoc = user?.user ?? user;
  const role = userDoc?.role;

  useEffect(() => {
    if (!EDUCAMAKER_STATUS_BANNER || !accessToken) return undefined;
    if (role !== "teacher" && role !== "institution") return undefined;
    let alive = true;
    fetch(`${ENV.BASE_API}/educamaker/status`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (alive && data) setStatus(data);
      })
      .catch(() => null);
    return () => {
      alive = false;
    };
  }, [accessToken, role]);

  if (dismissed || !status) return null;
  const { state, daysLeft, channel, expiredAt } = status;
  if (state !== "grace" && state !== "cut" && state !== "past_due") return null;

  const date = expiredAt
    ? new Date(expiredAt).toLocaleDateString("es-ES", {
        day: "numeric",
        month: "long",
      })
    : "";

  let text;
  if (state === "grace") {
    text =
      daysLeft === 1
        ? t("educamakerBanner.graceLastDay", { date })
        : t("educamakerBanner.grace", { date, days: daysLeft });
  } else if (state === "cut") {
    text = t("educamakerBanner.cut");
  } else {
    text = t("educamakerBanner.pastDue");
  }

  const ctaHref = `${channel === "stripe" ? RENEW_URL : CONTACT_URL}&utm_content=${state}`;
  const ctaLabel =
    channel === "stripe"
      ? t("educamakerBanner.renewCta")
      : t("educamakerBanner.contactCta");

  return (
    <div
      className={`${styles.banner} ${state === "cut" ? styles.cut : styles.warn}`}
      role="status"
    >
      <span className={styles.text}>⚠️ {text}</span>
      <a
        className={styles.cta}
        href={ctaHref}
        target="_blank"
        rel="noopener noreferrer"
      >
        {ctaLabel}
      </a>
      <button
        type="button"
        className={styles.close}
        aria-label={t("educamakerBanner.dismiss")}
        onClick={() => setDismissed(true)}
      >
        ×
      </button>
    </div>
  );
}
